import { createCancelable } from "app/pages/workspace/sales/helpers";
import type { IFetchService } from "app/services/fetchService";

import type { IntegrationStatusResponse } from "../../../components/types";
import type { CRMListObjectsType, CrmFeedLevel } from "../../../types";

export type SalesforceToggleServiceType = {
    getExclusionFilterStatus(): Promise<{ value: boolean }>;
    putSfIntegrationLeadFilterStatus(state: boolean): Promise<IntegrationStatusResponse>;
    putSfAccountsToggle(state: boolean): Promise<IntegrationStatusResponse>;
    putSfContactsToggle(state: boolean): Promise<IntegrationStatusResponse>;
    putSfLeadsToggle(state: boolean): Promise<IntegrationStatusResponse>;
    putSfCompanyAsLeadsToggle(state: boolean): Promise<IntegrationStatusResponse>;
    setIsSfCrmLabelEnabled(state: boolean): Promise<IntegrationStatusResponse>;
    setIsSfFeedEnabled(state: boolean): Promise<IntegrationStatusResponse>;
    setIsSfCrmListEnabled(state: boolean): Promise<IntegrationStatusResponse>;
    setSfCrmListObjectsType(state: CRMListObjectsType): Promise<IntegrationStatusResponse>;
    setSfCrmFeedLevel(value: CrmFeedLevel): Promise<IntegrationStatusResponse>;
};

export const salesforceTogglesService = (fetchService: IFetchService): SalesforceToggleServiceType => ({
    getExclusionFilterStatus: createCancelable((signal: AbortSignal): Promise<{ value: boolean }> => {
        return fetchService
            .get<{ value: boolean } | null>(`sales-api/salesforce/leads-filter-toggle`, undefined, {
                cancellation: signal,
            })
            .then((res) => ({ value: res?.value ?? false }));
    }),
    putSfContactsToggle: createCancelable((signal: AbortSignal, state: boolean): Promise<IntegrationStatusResponse> => {
        return fetchService.put(`sales-api/salesforce/contact-export-toggle?value=${state}`, undefined, {
            cancellation: signal,
        });
    }),
    putSfAccountsToggle: createCancelable((signal: AbortSignal, state: boolean): Promise<IntegrationStatusResponse> => {
        return fetchService.put(`sales-api/salesforce/export-toggle?value=${state}`, undefined, {
            cancellation: signal,
        });
    }),
    putSfLeadsToggle: createCancelable((signal: AbortSignal, state: boolean): Promise<IntegrationStatusResponse> => {
        return fetchService.put(`sales-api/salesforce/leads-export-toggle?value=${state}`, undefined, {
            cancellation: signal,
        });
    }),
    putSfCompanyAsLeadsToggle: createCancelable(
        (signal: AbortSignal, state: boolean): Promise<IntegrationStatusResponse> => {
            return fetchService.put(`sales-api/salesforce/company-as-lead-export-toggle?value=${state}`, undefined, {
                cancellation: signal,
            });
        },
    ),
    putSfIntegrationLeadFilterStatus: createCancelable(
        (signal: AbortSignal, state: boolean): Promise<IntegrationStatusResponse> => {
            return fetchService.put(`sales-api/salesforce/leads-filter-toggle?value=${state}`, undefined, {
                cancellation: signal,
            });
        },
    ),
    setIsSfCrmLabelEnabled: createCancelable(
        (signal: AbortSignal, state: boolean): Promise<IntegrationStatusResponse> => {
            return fetchService.put(`/sales-api/salesforce/crm-labels-toggle?value=${state}`, undefined, {
                cancellation: signal,
            });
        },
    ),
    setIsSfFeedEnabled: createCancelable((signal: AbortSignal, state: boolean): Promise<IntegrationStatusResponse> => {
        return fetchService.put(`/sales-api/salesforce/crm-feed-toggle?value=${state}`, undefined, {
            cancellation: signal,
        });
    }),
    setIsSfCrmListEnabled: createCancelable(
        (signal: AbortSignal, state: boolean): Promise<IntegrationStatusResponse> => {
            return fetchService.put(`/sales-api/salesforce/crm-list-toggle?value=${state}`, undefined, {
                cancellation: signal,
            });
        },
    ),
    setSfCrmListObjectsType: createCancelable(
        (signal: AbortSignal, value: CRMListObjectsType): Promise<IntegrationStatusResponse> => {
            return fetchService.put(`/sales-api/salesforce/crm-list/monitor-types?value=${value}`, undefined, {
                cancellation: signal,
            });
        },
    ),
    setSfCrmFeedLevel: createCancelable(
        (signal: AbortSignal, value: CrmFeedLevel): Promise<IntegrationStatusResponse> => {
            return fetchService.put(`/sales-api/salesforce/crm-feed-level?value=${value}`, undefined, {
                cancellation: signal,
            });
        },
    ),
});
